import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';

export interface UserPreferences {
  id: string;
  userId: string;
  emailNotifications: boolean;
  pushNotifications: boolean;
  weeklyReports: boolean;
  usageHints: boolean;
  userExperience: boolean;
  dataSharing: boolean;
  marketing: boolean;
  cookies: boolean;
  whatsappNotifications: boolean;
  whatsappNumber: string;
  ticketEmailScope: string;
  ticketNotificationMethod: string;
  ticketNotificationTime: string;
  ticketDeadlineReminder: string;
  ticketDeadlineReminderTime: string;
  ticketDeadlineScope: string;
  ticketNoSelfChanges: boolean;
  ticketContractorOnly: boolean;
  dateFormat?: string;
  preferredLanguage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PreferencesUpdate {
  emailNotifications?: boolean;
  pushNotifications?: boolean;
  weeklyReports?: boolean;
  usageHints?: boolean;
  userExperience?: boolean;
  dataSharing?: boolean;
  marketing?: boolean;
  cookies?: boolean;
  whatsappNotifications?: boolean;
  whatsappNumber?: string;
  ticketEmailScope?: string;
  ticketNotificationMethod?: string;
  ticketNotificationTime?: string;
  ticketDeadlineReminder?: string;
  ticketDeadlineReminderTime?: string;
  ticketDeadlineScope?: string;
  ticketNoSelfChanges?: boolean;
  ticketContractorOnly?: boolean;
  dateFormat?: string;
  preferredLanguage?: string | null;
}

const QUERY_KEYS = {
  preferences: ['preferences'] as const,
};

export function useUserPreferences(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.preferences,
    queryFn: async () => {
      const response = await apiClient.getPreferences();
      return (response as { data?: UserPreferences }).data as UserPreferences;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: options?.enabled ?? true,
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (preferences: PreferencesUpdate) => {
      const response = await apiClient.updatePreferences(preferences);
      return (response as { data?: UserPreferences }).data as UserPreferences;
    },
    onMutate: async (newPreferences) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.preferences });

      // Snapshot the previous value
      const previousPreferences = queryClient.getQueryData<UserPreferences>(QUERY_KEYS.preferences);

      // Optimistically update to the new value
      if (previousPreferences) {
        queryClient.setQueryData<UserPreferences>(QUERY_KEYS.preferences, {
          ...previousPreferences,
          ...newPreferences,
        });
      }

      // Return a context object with the snapshotted value
      return { previousPreferences };
    },
    onSuccess: (data) => {
      // Update cache with the returned data from server
      queryClient.setQueryData(QUERY_KEYS.preferences, data);
    },
    onError: (err, newPreferences, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousPreferences) {
        queryClient.setQueryData(QUERY_KEYS.preferences, context.previousPreferences);
      }
      
      toast({
        title: 'Error',
        description: 'Failed to update preferences. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
